/* 

* Event driven structure

* Using variant:add and line-item:change

* first qualified item will be added then the gift item can be add

*/

(() => {
  'use strict';

  class GiftManager {
    constructor() {
      this.isProcessing = false;
      this.pendingQueue = [];
      this.debug = window.DD?.debug || true;
      this.processingKeys = new Set();

      this.init();
    }

    // init() {
    //   document.documentElement.addEventListener('variant:add', this.onVariantAdd.bind(this));
    //   document.documentElement.addEventListener('line-item:change', this.onLineItemChange.bind(this));

    //   if (document.readyState === 'loading') {
    //     document.addEventListener('DOMContentLoaded', () => this.reconcileCart());
    //   } else {
    //     this.reconcileCart();
    //   }

    //   this.log('GiftManager initialized');
    // }

    init() {
      document.documentElement.addEventListener(
        "variant:add",
        this.onVariantAdd.bind(this)
      );

      document.documentElement.addEventListener(
        "line-item:change",
        this.onLineItemChange.bind(this)
      );

      // document.documentElement.addEventListener(
      //   "cart:change",
      //   ({ detail }) => {
      //     if (detail?.cart?._giftManagerSource) return;

      //     this.reconcileCart(detail.cart);
      //   }
      // );

      document.documentElement.addEventListener(
        "cart:change",
        ({ detail }) => {
          if (detail?.cart?._giftManagerSource) return;
          this.queueAction(async () => {
            document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
            this.toggleCheckout(true);
            try {
              await this.reconcileCart();
            } finally {
              document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
              this.toggleCheckout(false);
            }
          });
        }
      );

      this.reconcileCart();
    }

    toggleCheckout(disabled = true) {
      const buttons = [
        ...document.querySelectorAll('button[form="cart-main-form"]'),
        ...document.querySelectorAll('button[name="checkout"]')
      ];

      buttons.forEach(button => {
        button.disabled = disabled;
        button.classList.toggle('is-disabled', disabled); // optional
        button.setAttribute('aria-disabled', disabled);
      });
    }
    /* ─── Event Handlers ─── */

    async onVariantAdd(event) {
      console.log("===== variant:add =====");
      console.log(event.detail);

      const { detail } = event;
      const { items = [] } = detail;

      this.log('variant:add received', items.map(i => i.title));

      const qualified = this.getQualifiedItems(items);
      if (!qualified.length) return;

      this.queueAction(async () => {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        this.toggleCheckout(true);
        try {
          const freshCart = await this.fetchCart();
          const matchedItems = this.matchItemsToCart(qualified, freshCart);
          await this.processGifts(matchedItems, freshCart, 'variant:add');
        } finally {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          this.toggleCheckout(false);
        }
      });
    }

    onLineItemChange({ detail }) {
      const { quantity, cart, key } = detail;
      this.log('line-item:change', key, quantity);

      if (!cart?.items) return;

      const changedItem = cart.items.find(i => i.key === key);

      if (!changedItem) {
        this.queueAction(async () => {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
          this.toggleCheckout(true);
          try {
            await this.reconcileCart(cart);
          } finally {
            document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
            this.toggleCheckout(false);
          }
        });
        return;
      }

      if (this.isGiftLineItem(changedItem)) {
        this.queueAction(async () => {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
          this.toggleCheckout(true);
          try {
            await this.handleGiftLineItemChange(changedItem, cart);
          } finally {
            document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
            this.toggleCheckout(false);
          }
        });
        return;
      }

      const config = this.getProductConfig(changedItem.product_id);
      if (!config?.gifts?.length) return;

      this.queueAction(async () => {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        this.toggleCheckout(true);
        try {
          await this.syncGiftQuantities(changedItem, cart);
        } finally {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          this.toggleCheckout(false);
        }
      });
    }

    /* ─── Core Logic ─── */

    async processGifts(qualifiedItems, cart, actionType) {
      const giftsToAdd = [];
      const giftsToUpdate = [];
      // Tracks gift variants already granted within this batch, so that adding
      // several "per order" qualifying items in one go doesn't each grant their own gift.
      const grantedPerOrder = new Set();

      for (const item of qualifiedItems) {
        if (this.processingKeys.has(item.key)) continue;
        this.processingKeys.add(item.key);

        for (const giftConfig of item.config.gifts) {
          if (!giftConfig.enable) continue;

          const giftVariantId = this.getGiftVariantId(giftConfig);
          if (!giftVariantId) {
            this.warn('No variant found for gift', giftConfig.internal_name);
            continue;
          }

          const isPerOrder = this.getQuantityRule(giftConfig) === 'per_order';

          if (isPerOrder && grantedPerOrder.has(giftVariantId)) {
            // Another item earlier in this same batch already grants this gift.
            continue;
          }

          const existingGift = isPerOrder
            ? this.findExistingGiftByVariant(cart, giftVariantId)
            : this.findExistingGift(cart, giftVariantId, item.key);

          if (existingGift) {
            const targetQty = this.calculateGiftQuantity(item, giftConfig);
            if (existingGift.quantity !== targetQty) {
              giftsToUpdate.push({
                key: existingGift.key,
                quantity: targetQty
              });
            }
            if (isPerOrder) grantedPerOrder.add(giftVariantId);
          } else {
            giftsToAdd.push({
              id: giftVariantId,
              quantity: this.calculateGiftQuantity(item, giftConfig),
              parent_line_key: item.key,
              properties: {
                _gift: 'true',
                _gift_parent_key: item.key,
                _gift_parent_id: item.product_id,
                _gift_message: giftConfig.gift_message || '',
                _gift_internal_name: giftConfig.internal_name || '',
                _gift_source: actionType,
                "Gift Message": giftConfig.gift_message || ''
              }
            });
            if (isPerOrder) grantedPerOrder.add(giftVariantId);
          }
        }
      }

      try {
        if (giftsToUpdate.length) await this.updateCartItems(giftsToUpdate);
        if (giftsToAdd.length) await this.addToCart(giftsToAdd);
      } finally {
        qualifiedItems.forEach(i => this.processingKeys.delete(i.key));
      }
    }

    async reconcileCart(cart = null) {
      const currentCart = cart || await this.fetchCart();
      if (!currentCart?.items) return;

      const parents = [];
      const gifts = [];
      const updates = [];
      const removals = [];

      for (const item of currentCart.items) {
        if (this.isGiftLineItem(item)) {
          gifts.push(item);
        } else {
          parents.push(item);
        }
      }

      const expected = new Map();

      // Group qualifying parents by the gift they resolve to, so quantity rules
      // that apply across the whole order (rather than per parent) can be applied.
      const giftGroups = new Map(); // variantId -> { config, entries: [parent, ...] }

      for (const parent of parents) {
        const config = this.getProductConfig(parent.product_id);
        if (!config?.gifts?.length) continue;

        for (const giftConfig of config.gifts) {
          if (!giftConfig.enable) continue;

          const variantId = this.getGiftVariantId(giftConfig);
          if (!variantId) continue;

          if (!giftGroups.has(variantId)) {
            giftGroups.set(variantId, { config: giftConfig, entries: [] });
          }
          giftGroups.get(variantId).entries.push(parent);
        }
      }

      for (const [variantId, group] of giftGroups) {
        if (this.getQuantityRule(group.config) === 'per_order') {
          // Cap this gift at a single quantity for the whole order, anchored to one parent.
          const anchor = this.pickAnchorParent(group.entries, gifts, variantId);
          if (anchor) {
            expected.set(`${anchor.key}::${variantId}`, {
              parentKey: anchor.key,
              variantId,
              quantity: 1,
              config: group.config
            });
          }
          continue;
        }

        for (const parent of group.entries) {
          expected.set(`${parent.key}::${variantId}`, {
            parentKey: parent.key,
            variantId,
            quantity: this.calculateGiftQuantity(parent, group.config),
            config: group.config
          });
        }
      }

      for (const gift of gifts) {
        const parentKey = gift.properties?._gift_parent_key;
        const variantId = gift.variant_id?.toString();
        const mapKey = `${parentKey}::${variantId}`;

        if (expected.has(mapKey)) {
          const exp = expected.get(mapKey);
          if (gift.quantity !== exp.quantity) {
            updates.push({ key: gift.key, quantity: exp.quantity });
          }
          expected.delete(mapKey);
        } else {
          removals.push(gift.key);
        }
      }

      const additions = [];
      for (const [, exp] of expected) {
        const parent = currentCart.items.find(i => i.key === exp.parentKey);
        if (!parent) continue;

        additions.push({
          id: exp.variantId,
          quantity: exp.quantity,
          parent_line_key: exp.parentKey,
          properties: {
            _gift: 'true',
            _gift_parent_key: exp.parentKey,
            _gift_parent_id: parent.product_id,
            _gift_message: exp.config.gift_message || '',
            _gift_internal_name: exp.config.internal_name || '',
            _gift_source: 'reconcile',
            "Gift Message": exp.config.gift_message || ''
          }
        });
      }

      if (removals.length) await this.removeFromCart(removals);
      if (updates.length) await this.updateCartItems(updates);
      if (additions.length) await this.addToCart(additions);
    }

    async syncGiftQuantities(parentItem, cart) {
      const config = this.getProductConfig(parentItem.product_id);
      if (!config?.gifts?.length) return;

      const updates = [];
      const additions = [];

      for (const giftConfig of config.gifts) {
        if (!giftConfig.enable) continue;

        const variantId = this.getGiftVariantId(giftConfig);
        const isPerOrder = this.getQuantityRule(giftConfig) === 'per_order';
        const existingGift = isPerOrder
          ? this.findExistingGiftByVariant(cart, variantId)
          : this.findExistingGift(cart, variantId, parentItem.key);

        if (existingGift) {
          const targetQty = this.calculateGiftQuantity(parentItem, giftConfig);
          if (existingGift.quantity !== targetQty) {
            updates.push({ key: existingGift.key, quantity: targetQty });
          }
        } else {
          additions.push({ ...parentItem, config });
        }
      }

      if (additions.length) {
        await this.processGifts(additions, cart, 'sync');
      } else if (updates.length) {
        await this.updateCartItems(updates);
      }
    }

    async handleGiftLineItemChange(giftItem, cart) {
      const parentKey = giftItem.properties?._gift_parent_key;
      if (!parentKey) return;

      const parentItem = cart.items.find(i => i.key === parentKey);

      if (!parentItem) {
        await this.removeFromCart([giftItem.key]);
        return;
      }

      const config = this.getProductConfig(parentItem.product_id);
      const giftConfig = config?.gifts?.find(g => {
        const vid = this.getGiftVariantId(g);
        return vid === giftItem.variant_id?.toString();
      });

      if (!giftConfig?.enable) {
        await this.removeFromCart([giftItem.key]);
        return;
      }

      const expectedQty = this.calculateGiftQuantity(parentItem, giftConfig);
      if (giftItem.quantity !== expectedQty) {
        await this.updateCartItems([{ key: giftItem.key, quantity: expectedQty }]);
      }
    }

    /* ─── Cart Page Check ─── */

    isCartPage() {
      return window.location.pathname === '/cart' ||
             window.location.pathname === `${Shopify.routes?.root || '/'}cart`;
    }

    /* ─── Section Bundling ─── */

    getBundledSections() {
      const sectionsToBundle = new Set();
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));
      const sections = [...sectionsToBundle].join(',');
      return sections || null;
    }

    /* ─── Cart API ─── */

    async addToCart(items) {
      if (!items.length) return;
      this.log('POST /cart/add.js', items);

      const body = { items };
      const sections = this.getBundledSections();
      if (sections) body.sections = sections;

      const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || `Add failed: ${res.status}`);
      }

      const responseJson = await res.json();

      // if (this.isCartPage()) {
      //   window.location.reload();
      //   return;
      // }

      const cartContent = await this.fetchCart();
      cartContent.sections = responseJson.sections;

      this.emitCartChange('gift:add', cartContent);
      return cartContent;
    }

    async updateCartItems(updates) {
      if (!updates.length) return;
      this.log('POST /cart/update.js', updates);

      const updatesMap = {};
      updates.forEach(u => { updatesMap[u.key] = u.quantity; });

      const body = { updates: updatesMap };
      const sections = this.getBundledSections();
      if (sections) body.sections = sections;

      const res = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error(`Update failed: ${res.status}`);

      const responseJson = await res.json();

      if (this.isCartPage()) {
        window.location.reload();
        return;
      }

      const cartContent = await this.fetchCart();
      cartContent.sections = responseJson.sections;

      this.emitCartChange('gift:update', cartContent);
      return cartContent;
    }

    async removeFromCart(keys) {
      if (!keys.length) return;
      this.log('POST /cart/update.js (remove)', keys);

      const updates = {};
      keys.forEach(k => { updates[k] = 0; });

      const body = { updates };
      const sections = this.getBundledSections();
      if (sections) body.sections = sections;

      const res = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error(`Remove failed: ${res.status}`);

      const responseJson = await res.json();

      if (this.isCartPage()) {
        window.location.reload();
        return;
      }

      const cartContent = await this.fetchCart();
      cartContent.sections = responseJson.sections;

      this.emitCartChange('gift:remove', cartContent);
      return cartContent;
    }

    async fetchCart() {
      const res = await fetch(`${Shopify.routes.root}cart.js`);
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      return res.json();
    }

    /* ─── Helpers ─── */

    getQualifiedItems(items = []) {
      return items
        .map(item => ({ ...item, config: this.getProductConfig(item.product_id) }))
        .filter(item => item.config?.gifts?.some(g => g.enable));
    }

    matchItemsToCart(qualifiedItems, cart) {
      return qualifiedItems.map(item => {
        const exact = cart.items.find(i => i.key === item.key);
        if (exact) return { ...item, key: exact.key, quantity: exact.quantity };

        const matched = cart.items.find(i =>
          i.variant_id === item.variant_id &&
          i.product_id === item.product_id
        );
        if (matched) return { ...item, key: matched.key, quantity: matched.quantity };

        return item;
      });
    }

    getProductConfig(productId) {
      return window.DD?.productConfigs?.[productId] || null;
    }

    getGiftVariantId(giftConfig) {
      const gp = giftConfig?.gift_product;
      if (gp && typeof gp === 'object') {
        return gp.variants?.[0]?.id?.toString() || null;
      }
      if (typeof gp === 'string') {
        const match = gp.match(/\/ProductVariant\/(\d+)$/);
        return match ? match[1] : gp;
      }
      return null;
    }

    calculateGiftQuantity(parentItem, giftConfig) {
      if (this.getQuantityRule(giftConfig) === 'per_order') return 1;
      return giftConfig.enable_multiple_quantity ? (parentItem.quantity || 1) : 1;
    }

    // Reads the "Quantity rule" field from the Gift Item metaobject.
    // 'per_order' caps the gift at 1 for the whole order; anything else
    // (including blank/unrecognized, for backwards compatibility with existing
    // Gift Item entries) preserves the original per-parent-item behavior.
    getQuantityRule(giftConfig) {
      const rule = (giftConfig?.quantity_rule || '').toString().trim().toLowerCase();
      return rule === 'per_order' ? 'per_order' : 'per_qualifying_item';
    }

    findExistingGift(cart, variantId, parentKey) {
      if (!cart?.items) return null;
      return cart.items.find(item =>
        this.isGiftLineItem(item) &&
        item.properties?._gift_parent_key === parentKey &&
        item.variant_id?.toString() === variantId
      );
    }

    // Like findExistingGift, but matches on the gift variant alone regardless of
    // which parent it's attached to. Used for the 'per_order' quantity rule, where
    // a single gift line item is shared across every qualifying parent in the order.
    findExistingGiftByVariant(cart, variantId) {
      if (!cart?.items) return null;
      return cart.items.find(item =>
        this.isGiftLineItem(item) &&
        item.variant_id?.toString() === variantId
      );
    }

    // Picks which qualifying parent a 'per_order' gift should be anchored to.
    // Prefers the parent already holding the gift (if it still qualifies) to avoid
    // needless remove/re-add churn on reconcile, otherwise falls back to the first
    // qualifying parent.
    pickAnchorParent(entries, gifts, variantId) {
      const existingGift = gifts.find(g => g.variant_id?.toString() === variantId);
      if (existingGift) {
        const currentParentKey = existingGift.properties?._gift_parent_key;
        const stillQualifies = entries.find(p => p.key === currentParentKey);
        if (stillQualifies) return stillQualifies;
      }
      return entries[0] || null;
    }

    isGiftLineItem(item) {
      return item.properties?._gift === 'true' || item.properties?._gift === true;
    }

    emitCartChange(baseEvent, cart) {
      if (cart) cart._giftManagerSource = true;
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: `gift-manager:${baseEvent}`, cart }
      }));
    }

    queueAction(action) {
      this.pendingQueue.push(action);
      clearTimeout(this.processingDebounce);
      this.processingDebounce = setTimeout(() => this.processQueue(), 50);
    }

    async processQueue() {
      if (this.isProcessing || !this.pendingQueue.length) return;
      this.isProcessing = true;

      while (this.pendingQueue.length) {
        const action = this.pendingQueue.shift();
        try {
          await action();
        } catch (err) {
          this.error('Queue action failed:', err);
        }
      }

      this.isProcessing = false;
    }

    log(...args) { if (this.debug) console.log('[GiftManager]', ...args); }
    warn(...args) { console.warn('[GiftManager]', ...args); }
    error(...args) { console.error('[GiftManager]', ...args); }
  }

  if (!window.DD) window.DD = {};
  window.DD.GiftManager = new GiftManager();
})();